// src/pages/activities/AddActivity.js
import React, { useContext, useState, useRef, useMemo, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "leaflet/dist/leaflet.css";
import {
  ArrowClockwise,
  Send,
  GeoAltFill,
  CheckCircleFill,
  PlusCircle,
  Trash3Fill,
} from "react-bootstrap-icons";
import { MapContainer, TileLayer, Marker, useMapEvents } from "react-leaflet";
import L from "leaflet";

import ChatbotButton from "../../components/common/ChatbotButton.js";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import { AddActivityRequest } from "../../services/activityService.js";
import PopupMessage from "../../components/common/PopupMessage.js";
import { AuthContext } from "../../context/AuthContext.js";

// --- 1. Fix Leaflet Marker Icon ---
const markerIcon = new L.Icon({
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

// --- 2. Constants ---
export const faculties = {
  all: "ALL",
  computerScience: "COMPUTER_SCIENCE",
  medicine: "MEDICINE",
  dentistry: "DENTISTRY",
  pharmacy: "PHARMACY",
  engineering: "ENGINEERING",
  businessAdministration: "BUSINESS_ADMINISTRATION",
  mediaAndAppliedLanguages: "MEDIA_AND_APPLIED_LANGUAGES",
  nursing: "NURSING",
  physiotherapy: "PHYSIOTHERAPY",
  fineArts: "FINE_ARTS",
  // ... (باقي الكليات زي ما هي)
};

export const levels = {
  // all: "ALL", // يفضل نشيل ALL من هنا عشان هنختار مستويات محددة، أو نخليها بس في الـ Loop نتجاهلها
  level1: "LEVEL_1",
  level2: "LEVEL_2",
  level3: "LEVEL_3",
  level4: "LEVEL_4",
  level5: "LEVEL_5",
  level6: "LEVEL_6",
  level7: "LEVEL_7",
};

export const facultyLevelsMap = {
  COMPUTER_SCIENCE: 4,
  ENGINEERING: 5,
  MEDICINE: 7,
  DENTISTRY: 5,
  PHARMACY: 5,
  NURSING: 4,
  PHYSIOTHERAPY: 5,
  FINE_ARTS: 4,
  BUSINESS_ADMINISTRATION: 4,
  MEDIA_AND_APPLIED_LANGUAGES: 4,
};

// --- 3. Helper Components for Map ---
function LocationPicker({ onSelect }) {
  useMapEvents({
    click(e) {
      const { lat, lng } = e.latlng;
      onSelect({ lat, lng });
    },
  });
  return null;
}

function MapRecenter({ lat, lng }) {
  const map = useMapEvents({});
  useEffect(() => {
    if (lat && lng) {
      map.setView([lat, lng], 18, { animate: true });
    }
  }, [lat, lng, map]);
  return null;
}

// --- 4. Main Component ---
export default function AddActivity() {
  const { user } = useContext(AuthContext);

  const markerRef = useRef(null);

  // State
  const [form, setForm] = useState({
    activityName: "",
    activityType: "",
    locationName: "",
    coordinates: [],
    capacity: "",
    targetedGender: "",
    targetedFaculty: user?.faculty,
    targetedLevel: [], // <--- تعديل 1: تحويلها لمصفوفة عشان تقبل أكتر من قيمة
    description: "",
    specialInstructions: "",
    activityPoster: null,
    links: [],
    registrationOpenAt: "",
    registrationCloseAt: "",
    activityStartAt: "",
    activityEndAt: "",
  });

  const [loading, setLoading] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [showPosterPreview, setShowPosterPreview] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [locationPicked, setLocationPicked] = useState(false);
  const [popup, setPopup] = useState({ show: false, message: "", type: "" });

  // --- Handlers ---

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // <--- تعديل 2: دالة خاصة للتعامل مع الـ Checkboxes للمستويات
  const handleLevelChange = (e) => {
    const { value, checked } = e.target;
    setForm((prev) => {
      let newLevels = [...prev.targetedLevel];
      if (checked) {
        // إضافة المستوى لو مش موجود
        if (!newLevels.includes(value)) newLevels.push(value);
      } else {
        // حذف المستوى
        newLevels = newLevels.filter((lvl) => lvl !== value);
      }
      return { ...prev, targetedLevel: newLevels };
    });
  };

  const handlePosterChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (file) setForm((prev) => ({ ...prev, activityPoster: file }));
  };

  const handleMapClick = (pos) => {
    setSelectedPosition(pos);
    setForm((prev) => ({ ...prev, coordinates: [pos.lat, pos.lng] }));
  };

  const eventHandlers = useMemo(
    () => ({
      dragend() {
        const marker = markerRef.current;
        if (marker != null) {
          const { lat, lng } = marker.getLatLng();
          setSelectedPosition({ lat, lng });
          setForm((prev) => ({ ...prev, coordinates: [lat, lng] }));
        }
      },
    }),
    [],
  );

  const handleConfirmLocation = () => {
    if (selectedPosition) {
      setLocationPicked(true);
      setShowMap(false);
      setPopup({
        show: true,
        message: "Location selected successfully!",
        type: "success",
      });
    }
  };

  // Links Handlers
  const handleAddLink = () => {
    setForm((prev) => ({
      ...prev,
      links: [...prev.links, { title: "", url: "" }],
    }));
  };

  const handleRemoveLink = (index) => {
    setForm((prev) => ({
      ...prev,
      links: prev.links.filter((_, i) => i !== index),
    }));
  };

  const handleLinkChange = (index, field, value) => {
    setForm((prev) => ({
      ...prev,
      links: prev.links.map((l, i) =>
        i === index ? { ...l, [field]: value } : l,
      ),
    }));
  };

  // --- Validation ---
  const validateDates = () => {
    const {
      registrationOpenAt,
      registrationCloseAt,
      activityStartAt,
      activityEndAt,
    } = form;

    if (registrationCloseAt <= registrationOpenAt) {
      setPopup({
        show: true,
        message: "Registration closing date must be AFTER opening date.",
        type: "error",
      });
      return false;
    }
    if (activityStartAt <= registrationCloseAt) {
      setPopup({
        show: true,
        message: "Activity should start AFTER registration closes.",
        type: "error",
      });
      return false;
    }
    if (activityEndAt <= activityStartAt) {
      setPopup({
        show: true,
        message: "Activity end time must be AFTER start time.",
        type: "error",
      });
      return false;
    }
    return true;
  };

  const validateForm = () => {
    const requiredFields = [
      "activityName",
      "activityType",
      "locationName",
      "capacity",
      "targetedGender",
      "targetedFaculty",
      "description", // شيلنا targetedLevel من هنا عشان هنتحقق منه لوحده
      "activityPoster",
      "registrationOpenAt",
      "registrationCloseAt",
      "activityStartAt",
      "activityEndAt",
    ];

    for (let field of requiredFields) {
      if (!form[field] || form[field].toString().trim() === "") {
        setPopup({
          show: true,
          message: `Please fill in all required fields. Missing: ${field}`,
          type: "error",
        });
        return false;
      }
    }

    // <--- تعديل 3: التحقق أن فيه على الأقل مستوى واحد تم اختياره
    if (form.targetedLevel.length === 0) {
      setPopup({
        show: true,
        message: "Please select at least one level.",
        type: "error",
      });
      return false;
    }

    if (!Array.isArray(form.coordinates) || form.coordinates.length !== 2) {
      setPopup({
        show: true,
        message: "Please select a location on the map.",
        type: "error",
      });
      return false;
    }

    if (Number(form.capacity) <= 0) {
      setPopup({
        show: true,
        message: "Capacity must be a positive number.",
        type: "error",
      });
      return false;
    }

    if (!validateDates()) return false;

    return true;
  };

  // --- Submit ---
  // const handleSubmit = async (e) => {
  //   e.preventDefault();
  //   if (!validateForm()) return;
  //   setLoading(true);

  //   try {
  //     const formData = new FormData();
  //     formData.append("activityPoster", form.activityPoster);
  //     formData.append("activityName", form.activityName);
  //     formData.append("activityType", form.activityType);
  //     formData.append("description", form.description);
  //     formData.append("specialInstructions", form.specialInstructions);
  //     formData.append("locationName", form.locationName);
  //     formData.append("coordinates", JSON.stringify(form.coordinates));
  //     formData.append("capacity", Number(form.capacity));
  //     formData.append("targetedFaculty", form.targetedFaculty);
  //     formData.append("targetedGender", form.targetedGender);

  //     // <--- تعديل 4: إرسال المصفوفة كـ JSON String
  //     // الباك إند هيحتاج يعمل JSON.parse عشان يرجعها مصفوفة
  //     formData.append("targetedLevel", JSON.stringify(form.targetedLevel));

  //     formData.append("links", JSON.stringify(form.links || []));
  //     formData.append("registrationOpenAt", form.registrationOpenAt);
  //     formData.append("registrationCloseAt", form.registrationCloseAt);
  //     formData.append("activityStartAt", form.activityStartAt);
  //     formData.append("activityEndAt", form.activityEndAt);

  //     const addActivityRes = await AddActivityRequest(formData);
  //     setPopup({
  //       show: true,
  //       message: addActivityRes?.message || "Activity added successfully!",
  //       type: "success",
  //     });

  //     // Reset Form
  //     setForm({
  //       activityName: "",
  //       activityType: "",
  //       locationName: "",
  //       coordinates: [],
  //       capacity: "",
  //       targetedGender: "",
  //       targetedFaculty: user?.faculty || "",
  //       targetedLevel: [], // Reset to empty array
  //       description: "",
  //       specialInstructions: "",
  //       activityPoster: null,
  //       links: [],
  //       registrationOpenAt: "",
  //       registrationCloseAt: "",
  //       activityStartAt: "",
  //       activityEndAt: "",
  //     });

  //     setLocationPicked(false);
  //     setSelectedPosition(null);
  //   } catch (err) {
  //     console.error("Error sending activity:", err);
  //     setPopup({
  //       show: true,
  //       message: err?.message || "Failed to add activity.",
  //       type: "error",
  //     });
  //   } finally {
  //     setLoading(false);
  //   }
  // };
  // --- Submit ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);

    try {
      // 1. دالة التحويل لـ UTC (زي ما عملنا في التعديل)
      const toUTC = (dateStr) => {
        if (!dateStr) return "";
        return new Date(dateStr).toISOString();
      };

      const formData = new FormData();
      formData.append("activityPoster", form.activityPoster);
      formData.append("activityName", form.activityName);
      formData.append("activityType", form.activityType);
      formData.append("description", form.description);
      formData.append("specialInstructions", form.specialInstructions);
      formData.append("locationName", form.locationName);
      formData.append("coordinates", JSON.stringify(form.coordinates));
      formData.append("capacity", Number(form.capacity));
      formData.append("targetedFaculty", form.targetedFaculty);
      formData.append("targetedGender", form.targetedGender);
      formData.append("targetedLevel", JSON.stringify(form.targetedLevel));
      formData.append("links", JSON.stringify(form.links || []));

      // 2. هنا التعديل: بنحول التواريخ لـ UTC قبل الإرسال
      formData.append("registrationOpenAt", toUTC(form.registrationOpenAt));
      formData.append("registrationCloseAt", toUTC(form.registrationCloseAt));
      formData.append("activityStartAt", toUTC(form.activityStartAt));
      formData.append("activityEndAt", toUTC(form.activityEndAt));

      const addActivityRes = await AddActivityRequest(formData);
      setPopup({
        show: true,
        message: addActivityRes?.message || "Activity added successfully!",
        type: "success",
      }); // Reset Form

      setForm({
        activityName: "",
        activityType: "",
        locationName: "",
        coordinates: [],
        capacity: "",
        targetedGender: "",
        targetedFaculty: user?.faculty || "",
        targetedLevel: [],
        description: "",
        specialInstructions: "",
        activityPoster: null,
        links: [],
        registrationOpenAt: "",
        registrationCloseAt: "",
        activityStartAt: "",
        activityEndAt: "",
      });

      setLocationPicked(false);
      setSelectedPosition(null);
    } catch (err) {
      console.error("Error sending activity:", err);
      setPopup({
        show: true,
        message: err?.message || "Failed to add activity.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Filter Levels Logic
  const userFaculty = user?.faculty;
  const maxLevel = facultyLevelsMap[userFaculty];
  const filteredLevels = Object.values(levels).filter((lvl) => {
    if (lvl === "ALL") return false; // <--- تعديل 5: نتجاهل "ALL" هنا لأننا هنختار بالواحد
    const levelNumber = Number(lvl.split("_")[1]);
    return levelNumber <= maxLevel;
  });

  return (
    <div
      className="container-fluid min-vh-100 py-5"
      style={{
        backgroundColor: "#f8f9fa",
        fontFamily: "'Poppins', sans-serif",
        marginTop: "56px",
      }}
    >
      {popup.show && (
        <PopupMessage
          type={popup.type}
          message={popup.message}
          onClose={() => setPopup({ show: false, message: "", type: "" })}
        />
      )}

      <h2 className="text-center fw-bold mb-5 text-primary">
        Add New Activity
      </h2>

      <form
        onSubmit={handleSubmit}
        className="mx-auto"
        style={{ maxWidth: "1300px" }}
      >
        <div className="row g-4">
          {/* ... (باقي الحقول زي ما هي) ... */}
          {/* Activity Name */}
          <div className="col-md-6">
            <label className="form-label fw-semibold text-primary">
              Activity Name
            </label>
            <input
              type="text"
              name="activityName"
              className="form-control border-primary shadow-sm"
              placeholder="Enter The Activity Name"
              value={form.activityName}
              onChange={handleChange}
            />
          </div>

          <div className="col-md-6">
            <label className="form-label fw-semibold text-primary">
              Activity Type
            </label>
            <select
              className="form-select border-primary shadow-sm"
              name="activityType"
              value={form.activityType}
              onChange={handleChange}
            >
              <option value="">Select Activity Type</option>
              <option>SPORTS</option>
              <option>ACADEMIC</option>
              <option>ENTERTAINMENT</option>
              <option>COMPETITIONS</option>
            </select>
          </div>

          <div className="col-md-6">
            <label className="form-label fw-semibold text-primary">
              Location / Venue
            </label>
            <div className="d-flex gap-2 align-items-center">
              <input
                type="text"
                name="locationName"
                className="form-control border-primary shadow-sm"
                placeholder="Enter location manually (e.g. Building C)"
                value={form.locationName}
                onChange={handleChange}
              />
              <button
                type="button"
                className={`btn fw-semibold shadow-sm d-flex align-items-center gap-2 ${locationPicked ? "btn-success" : "btn-outline-primary"}`}
                onClick={() => setShowMap(true)}
                style={{
                  transition: "all 0.3s ease",
                  borderRadius: "50px",
                  whiteSpace: "nowrap",
                }}
              >
                {locationPicked ? (
                  <>
                    <CheckCircleFill /> Picked
                  </>
                ) : (
                  <>
                    <GeoAltFill /> Pick
                  </>
                )}
              </button>
            </div>
          </div>

          <div className="col-md-3">
            <label className="form-label fw-semibold text-primary">
              Gender
            </label>
            <select
              className="form-select border-primary shadow-sm"
              name="targetedGender"
              value={form.targetedGender}
              onChange={handleChange}
            >
              <option value="">Select Gender</option>
              <option>ALL</option>
              <option>MALE</option>
              <option>FEMALE</option>
            </select>
          </div>

          <div className="col-md-3">
            <label className="form-label fw-semibold text-primary">
              Capacity
            </label>
            <input
              type="number"
              name="capacity"
              className="form-control border-primary shadow-sm"
              placeholder="Limit"
              value={form.capacity}
              onChange={handleChange}
              min="1"
            />
          </div>

          <div className="col-md-6">
            <label className="form-label fw-semibold text-primary">
              College
            </label>
            <select
              className="form-select border-primary shadow-sm"
              name="targetedFaculty"
              value={form.targetedFaculty}
              disabled
            >
              <option value={userFaculty}>{userFaculty}</option>
            </select>
          </div>

          {/* <--- تعديل 6: تغيير الشكل في الـ UI من Select إلى Checkboxes */}
          <div className="col-md-6">
            <label className="form-label fw-semibold text-primary">
              Targeted Levels
            </label>
            <div className="card border-primary shadow-sm p-2">
              <div className="d-flex flex-wrap gap-3">
                {filteredLevels.map((lvl) => (
                  <div className="form-check" key={lvl}>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      value={lvl}
                      id={`lvl-${lvl}`}
                      checked={form.targetedLevel.includes(lvl)}
                      onChange={handleLevelChange}
                    />
                    <label
                      className="form-check-label user-select-none"
                      htmlFor={`lvl-${lvl}`}
                      style={{ cursor: "pointer" }}
                    >
                      {lvl}
                    </label>
                  </div>
                ))}
                {filteredLevels.length === 0 && (
                  <span className="text-muted small">
                    No levels available for this faculty.
                  </span>
                )}
              </div>
            </div>
            <div className="form-text text-muted">
              Select one or more levels allowed to join.
            </div>
          </div>

          {/* Date Fields ... (الباقي زي ما هو) */}
          <div className="col-md-6">
            <label className="form-label fw-semibold text-primary">
              Registration Opens At
            </label>
            <input
              type="datetime-local"
              name="registrationOpenAt"
              className="form-control border-primary shadow-sm"
              value={form.registrationOpenAt}
              onChange={handleChange}
            />
          </div>
          <div className="col-md-6">
            <label className="form-label fw-semibold text-primary">
              Registration Closes At
            </label>
            <input
              type="datetime-local"
              name="registrationCloseAt"
              className="form-control border-primary shadow-sm"
              value={form.registrationCloseAt}
              onChange={handleChange}
            />
          </div>
          <div className="col-md-6">
            <label className="form-label fw-semibold text-primary">
              Activity Starts At
            </label>
            <input
              type="datetime-local"
              name="activityStartAt"
              className="form-control border-primary shadow-sm"
              value={form.activityStartAt}
              onChange={handleChange}
            />
          </div>
          <div className="col-md-6">
            <label className="form-label fw-semibold text-primary">
              Activity Ends At
            </label>
            <input
              type="datetime-local"
              name="activityEndAt"
              className="form-control border-primary shadow-sm"
              value={form.activityEndAt}
              onChange={handleChange}
            />
          </div>

          <div className="col-md-6">
            <label className="form-label fw-semibold text-primary">
              Activity Description
            </label>
            <textarea
              className="form-control border-primary shadow-sm"
              name="description"
              rows={4}
              placeholder="Enter Activity Description"
              value={form.description}
              onChange={handleChange}
            />
          </div>

          <div className="col-md-6">
            <label className="form-label fw-semibold text-primary">
              Special Instructions
            </label>
            <textarea
              className="form-control border-primary shadow-sm"
              name="specialInstructions"
              rows={4}
              placeholder="Enter Special Instructions"
              value={form.specialInstructions}
              onChange={handleChange}
            />
          </div>

          <div className="col-12">
            <label className="form-label fw-semibold text-primary">
              Related Links
            </label>
            {form.links.map((link, index) => (
              <div key={index} className="d-flex gap-2 mb-2 align-items-center">
                <input
                  type="text"
                  className="form-control border-primary shadow-sm"
                  placeholder="Link Title (e.g., Intro Video)"
                  value={link.title}
                  onChange={(e) =>
                    handleLinkChange(index, "title", e.target.value)
                  }
                />
                <input
                  type="url"
                  className="form-control border-primary shadow-sm"
                  placeholder="Enter URL"
                  value={link.url}
                  onChange={(e) =>
                    handleLinkChange(index, "url", e.target.value)
                  }
                />
                <Button
                  variant="danger"
                  onClick={() => handleRemoveLink(index)}
                >
                  <Trash3Fill />
                </Button>
              </div>
            ))}
            <Button variant="outline-primary" onClick={handleAddLink}>
              <PlusCircle className="me-2" /> Add Link
            </Button>
          </div>

          <div className="col-md-6">
            <label className="form-label fw-semibold text-primary">
              Upload Poster
            </label>
            <div
              className="border border-primary border-2 rounded-3 d-flex flex-column align-items-center justify-content-center position-relative overflow-hidden shadow-sm"
              style={{
                minHeight: "200px",
                backgroundColor: "#f0f8ff",
                borderStyle: "dashed",
                transition: "0.3s",
              }}
            >
              {form.activityPoster && (
                <img
                  src={URL.createObjectURL(form.activityPoster)}
                  alt="Preview"
                  className="w-100 h-100 object-fit-cover position-absolute top-0 start-0"
                  style={{ opacity: 0.3 }}
                />
              )}
              <input
                type="file"
                accept="image/*"
                onChange={handlePosterChange}
                id="posterInput"
                className="d-none"
              />
              <div className="position-relative z-2 d-flex gap-2">
                {!form.activityPoster ? (
                  <button
                    type="button"
                    onClick={() =>
                      document.getElementById("posterInput").click()
                    }
                    className="btn btn-outline-primary fw-semibold"
                  >
                    Upload Poster
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      className="btn btn-outline-success fw-semibold"
                      onClick={() => setShowPosterPreview(true)}
                    >
                      View Poster
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        document.getElementById("posterInput").click()
                      }
                      className="btn btn-outline-primary fw-semibold"
                    >
                      Change Poster
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="col-12 text-center mt-4">
            <button
              type="submit"
              className="btn btn-primary fw-semibold px-5 py-2 shadow-sm"
              disabled={loading}
              style={{ borderRadius: "50px" }}
            >
              {loading ? (
                <>
                  <ArrowClockwise className="spin me-2" /> Sending...
                </>
              ) : (
                <>
                  <Send className="me-2" /> Submit Activity
                </>
              )}
            </button>
          </div>
        </div>
      </form>

      {/* ... (المودال زي ما هو) ... */}
      <Modal
        show={showMap}
        onHide={() => setShowMap(false)}
        size="lg"
        centered
        backdrop="static"
      >
        {/* ... محتوى الخريطة ... */}
        <Modal.Header
          closeButton
          className="bg-primary text-white"
          style={{ borderBottom: "none" }}
        >
          <Modal.Title className="fw-semibold">
            Select Activity Location (High Precision)
          </Modal.Title>
        </Modal.Header>
        <Modal.Body
          className="p-3"
          style={{
            height: "500px",
            backgroundColor: "#f8f9fa",
            borderRadius: "8px",
          }}
        >
          {/* ... (نفس كود الخريطة السابق) ... */}
          <div
            className="d-flex flex-wrap justify-content-center justify-content-md-end gap-3 mb-3"
            style={{ zIndex: 1000 }}
          >
            <Button
              variant="outline-primary"
              className="fw-semibold d-flex align-items-center gap-2 shadow-sm"
              style={{
                borderRadius: "30px",
                minWidth: "190px",
                transition: "all 0.3s ease",
              }}
              onClick={() => {
                if (navigator.geolocation) {
                  const options = {
                    enableHighAccuracy: true,
                    timeout: 10000,
                    maximumAge: 0,
                  };
                  setPopup({
                    show: true,
                    message: "Requesting high-accuracy GPS...",
                    type: "info",
                  });
                  navigator.geolocation.getCurrentPosition(
                    (position) => {
                      const { latitude, longitude } = position.coords;
                      setPopup({
                        show: true,
                        message: "Location detected! Drag marker to adjust.",
                        type: "info",
                      });
                      setSelectedPosition({ lat: latitude, lng: longitude });
                      setForm((prev) => ({
                        ...prev,
                        coordinates: [latitude, longitude],
                      }));
                    },
                    (error) =>
                      setPopup({
                        show: true,
                        message: "Failed to get location. Enable GPS.",
                        type: "error",
                      }),
                    options,
                  );
                } else {
                  setPopup({
                    show: true,
                    message: "Geolocation not supported.",
                    type: "error",
                  });
                }
              }}
            >
              <GeoAltFill /> Use My Location
            </Button>

            <Button
              variant={selectedPosition ? "success" : "outline-secondary"}
              disabled={!selectedPosition}
              className={`fw-semibold d-flex align-items-center gap-2 shadow-sm ${selectedPosition ? "btn-success" : ""}`}
              style={{
                borderRadius: "30px",
                minWidth: "190px",
                transition: "all 0.3s ease",
              }}
              onClick={handleConfirmLocation}
            >
              <CheckCircleFill /> Confirm Selection
            </Button>
          </div>

          <div
            className="rounded-4 overflow-hidden shadow-sm"
            style={{
              height: "100%",
              minHeight: "400px",
              border: "2px solid #0d6efd33",
            }}
          >
            <MapContainer
              center={selectedPosition || [30.0444, 31.2357]}
              zoom={14}
              style={{ height: "100%", width: "100%" }}
              className="rounded-4"
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution="&copy; OpenStreetMap contributors"
              />
              <LocationPicker onSelect={handleMapClick} />
              {selectedPosition && (
                <MapRecenter
                  lat={selectedPosition.lat}
                  lng={selectedPosition.lng}
                />
              )}
              {selectedPosition && (
                <Marker
                  draggable={true}
                  eventHandlers={eventHandlers}
                  position={[selectedPosition.lat, selectedPosition.lng]}
                  ref={markerRef}
                  icon={markerIcon}
                />
              )}
            </MapContainer>
          </div>
        </Modal.Body>
        <Modal.Footer
          className="d-flex justify-content-center justify-content-md-end gap-3 bg-light"
          style={{ borderTop: "none" }}
        >
          <Button
            variant="secondary"
            onClick={() => setShowMap(false)}
            className="fw-semibold rounded-pill px-4"
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showPosterPreview}
        onHide={() => setShowPosterPreview(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Poster Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {form.activityPoster && (
            <img
              src={URL.createObjectURL(form.activityPoster)}
              alt="Poster Preview"
              className="img-fluid rounded shadow-sm"
            />
          )}
        </Modal.Body>
      </Modal>

      <ChatbotButton />
    </div>
  );
}

// // src/pages/activities/AddActivity.js
// import React, { useContext, useState, useRef, useMemo, useEffect } from "react";
// import "bootstrap/dist/css/bootstrap.min.css";
// import "leaflet/dist/leaflet.css";
// import {
//   ArrowClockwise,
//   Send,
//   GeoAltFill,
//   CheckCircleFill,
//   PlusCircle,
//   Trash3Fill,
// } from "react-bootstrap-icons";
// import { MapContainer, TileLayer, Marker, useMapEvents } from "react-leaflet";
// import L from "leaflet";

// import ChatbotButton from "../../components/common/ChatbotButton";
// import Modal from "react-bootstrap/Modal";
// import Button from "react-bootstrap/Button";
// import { AddActivityRequest } from "../../services/activityService";
// import PopupMessage from "../../components/common/PopupMessage.js";
// import { AuthContext } from "../../context/AuthContext.js";

// // --- 1. Fix Leaflet Marker Icon ---
// const markerIcon = new L.Icon({
//   iconUrl:
//     "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
//   iconRetinaUrl:
//     "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
//   shadowUrl:
//     "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
//   iconSize: [25, 41],
//   iconAnchor: [12, 41],
//   popupAnchor: [1, -34],
//   shadowSize: [41, 41],
// });

// // --- 2. Constants ---
// export const faculties = {
//   all: "ALL",
//   computerScience: "COMPUTER_SCIENCE",
//   engineering: "ENGINEERING",
//   medicine: "MEDICINE",
//   dentistry: "DENTISTRY",
//   pharmacy: "PHARMACY",
//   nursing: "NURSING",
//   physiotherapy: "PHYSIOTHERAPY",
//   fineArts: "FINE_ARTS",
//   businessAdministration: "BUSINESS_ADMINISTRATION",
//   mediaAndAppliedLanguages: "MEDIA_AND_APPLIED_LANGUAGES",
// };

// export const levels = {
//   all: "ALL",
//   level1: "LEVEL_1",
//   level2: "LEVEL_2",
//   level3: "LEVEL_3",
//   level4: "LEVEL_4",
//   level5: "LEVEL_5",
//   level6: "LEVEL_6",
//   level7: "LEVEL_7",
// };

// export const facultyLevelsMap = {
//   COMPUTER_SCIENCE: 4,
//   ENGINEERING: 5,
//   MEDICINE: 7,
//   DENTISTRY: 5,
//   PHARMACY: 5,
//   NURSING: 4,
//   PHYSIOTHERAPY: 5,
//   FINE_ARTS: 4,
//   BUSINESS_ADMINISTRATION: 4,
//   MEDIA_AND_APPLIED_LANGUAGES: 4,
// };

// // --- 3. Helper Components for Map ---

// // Handle Click on Map
// function LocationPicker({ onSelect }) {
//   useMapEvents({
//     click(e) {
//       const { lat, lng } = e.latlng;
//       onSelect({ lat, lng });
//     },
//   });
//   return null;
// }

// // Auto Zoom to Location (مهم جداً عشان يوريك المكان بدقة)
// function MapRecenter({ lat, lng }) {
//   const map = useMapEvents({});
//   useEffect(() => {
//     if (lat && lng) {
//       map.setView([lat, lng], 18, { animate: true }); // Zoom Level 18 (Very Close for Attendance)
//     }
//   }, [lat, lng, map]);
//   return null;
// }

// // --- 4. Main Component ---
// export default function AddActivity() {
//   const { user } = useContext(AuthContext);

//   // Ref for the marker instance (عشان نقدر نجيب مكانه بعد السحب)
//   const markerRef = useRef(null);

//   // State
//   const [form, setForm] = useState({
//     activityName: "",
//     activityType: "",
//     locationName: "",
//     coordinates: [],
//     capacity: "",
//     targetedGender: "",
//     targetedFaculty: user?.faculty,
//     targetedLevel: "",
//     description: "",
//     specialInstructions: "",
//     activityPoster: null,
//     links: [],
//     registrationOpenAt: "",
//     registrationCloseAt: "",
//     activityStartAt: "",
//     activityEndAt: "",
//   });

//   const [loading, setLoading] = useState(false);
//   const [showMap, setShowMap] = useState(false);
//   const [showPosterPreview, setShowPosterPreview] = useState(false);
//   const [selectedPosition, setSelectedPosition] = useState(null);
//   const [locationPicked, setLocationPicked] = useState(false);
//   const [popup, setPopup] = useState({ show: false, message: "", type: "" });

//   // --- Handlers ---

//   const handleChange = (e) => {
//     const { name, value } = e.target;
//     setForm((prev) => ({ ...prev, [name]: value }));
//   };

//   const handlePosterChange = (e) => {
//     const file = e.target.files && e.target.files[0];
//     if (file) setForm((prev) => ({ ...prev, activityPoster: file }));
//   };

//   // Map Click Handler
//   const handleMapClick = (pos) => {
//     setSelectedPosition(pos);
//     setForm((prev) => ({ ...prev, coordinates: [pos.lat, pos.lng] }));
//   };

//   // Draggable Marker Handler (Critical for precision)
//   // ده اللي بيخليك تسحب الدبوس وتجيب الإحداثيات الجديدة
//   const eventHandlers = useMemo(
//     () => ({
//       dragend() {
//         const marker = markerRef.current;
//         if (marker != null) {
//           const { lat, lng } = marker.getLatLng();
//           setSelectedPosition({ lat, lng });
//           setForm((prev) => ({ ...prev, coordinates: [lat, lng] }));
//         }
//       },
//     }),
//     []
//   );

//   const handleConfirmLocation = () => {
//     if (selectedPosition) {
//       setLocationPicked(true);
//       setShowMap(false);
//       setPopup({
//         show: true,
//         message: "Location selected successfully!",
//         type: "success",
//       });
//     }
//   };

//   // Links Handlers
//   const handleAddLink = () => {
//     setForm((prev) => ({
//       ...prev,
//       links: [...prev.links, { title: "", url: "" }],
//     }));
//   };

//   const handleRemoveLink = (index) => {
//     setForm((prev) => ({
//       ...prev,
//       links: prev.links.filter((_, i) => i !== index),
//     }));
//   };

//   const handleLinkChange = (index, field, value) => {
//     setForm((prev) => ({
//       ...prev,
//       links: prev.links.map((l, i) =>
//         i === index ? { ...l, [field]: value } : l,
//       ),
//     }));
//   };

//   // --- Validation ---

//   // التأكد من منطقية التواريخ
//   const validateDates = () => {
//     const { registrationOpenAt, registrationCloseAt, activityStartAt, activityEndAt } = form;

//     if (registrationCloseAt <= registrationOpenAt) {
//       setPopup({ show: true, message: "Registration closing date must be AFTER opening date.", type: "error" });
//       return false;
//     }
//     if (activityStartAt <= registrationCloseAt) {
//       setPopup({ show: true, message: "Activity should start AFTER registration closes.", type: "error" });
//       return false;
//     }
//     if (activityEndAt <= activityStartAt) {
//       setPopup({ show: true, message: "Activity end time must be AFTER start time.", type: "error" });
//       return false;
//     }
//     return true;
//   };

//   const validateForm = () => {
//     const requiredFields = [
//       "activityName", "activityType", "locationName", "capacity",
//       "targetedGender", "targetedFaculty", "targetedLevel", "description",
//       "activityPoster", "registrationOpenAt", "registrationCloseAt",
//       "activityStartAt", "activityEndAt",
//     ];

//     for (let field of requiredFields) {
//       if (!form[field] || form[field].toString().trim() === "") {
//         setPopup({
//           show: true,
//           message: `Please fill in all required fields. Missing: ${field}`,
//           type: "error",
//         });
//         return false;
//       }
//     }

//     if (!Array.isArray(form.coordinates) || form.coordinates.length !== 2) {
//       setPopup({ show: true, message: "Please select a location on the map.", type: "error" });
//       return false;
//     }

//     if (Number(form.capacity) <= 0) {
//       setPopup({ show: true, message: "Capacity must be a positive number.", type: "error" });
//       return false;
//     }

//     if (!validateDates()) return false;

//     return true;
//   };

//   // --- Submit ---
//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     if (!validateForm()) return;
//     setLoading(true);

//     try {
//       const formData = new FormData();
//       formData.append("activityPoster", form.activityPoster);
//       formData.append("activityName", form.activityName);
//       formData.append("activityType", form.activityType);
//       formData.append("description", form.description);
//       formData.append("specialInstructions", form.specialInstructions);
//       formData.append("locationName", form.locationName);
//       formData.append("coordinates", JSON.stringify(form.coordinates));
//       formData.append("capacity", Number(form.capacity));
//       formData.append("targetedFaculty", form.targetedFaculty);
//       formData.append("targetedGender", form.targetedGender);
//       formData.append("targetedLevel", form.targetedLevel);
//       formData.append("links", JSON.stringify(form.links || []));
//       formData.append("registrationOpenAt", form.registrationOpenAt);
//       formData.append("registrationCloseAt", form.registrationCloseAt);
//       formData.append("activityStartAt", form.activityStartAt);
//       formData.append("activityEndAt", form.activityEndAt);

//       const addActivityRes = await AddActivityRequest(formData);
//       setPopup({
//         show: true,
//         message: addActivityRes?.message || "Activity added successfully!",
//         type: "success",
//       });

//       // Reset Form
//       setForm({
//         activityName: "", activityType: "", locationName: "", coordinates: [],
//         capacity: "", targetedGender: "", targetedFaculty: user?.faculty || "", targetedLevel: "",
//         description: "", specialInstructions: "", activityPoster: null, links: [],
//         registrationOpenAt: "", registrationCloseAt: "", activityStartAt: "", activityEndAt: "",
//       });

//       setLocationPicked(false);
//       setSelectedPosition(null);
//     } catch (err) {
//       console.error("Error sending activity:", err);
//       setPopup({
//         show: true,
//         message: err?.message || "Failed to add activity.",
//         type: "error",
//       });
//     } finally {
//       setLoading(false);
//     }
//   };

//   // Filter Levels Logic
//   const userFaculty = user?.faculty;
//   const maxLevel = facultyLevelsMap[userFaculty];
//   const filteredLevels = Object.values(levels).filter((lvl) => {
//     if (lvl === "ALL") return true;
//     const levelNumber = Number(lvl.split("_")[1]);
//     return levelNumber <= maxLevel;
//   });

//   return (
//     <div
//       className="container-fluid min-vh-100 py-5"
//       style={{
//         backgroundColor: "#f8f9fa",
//         fontFamily: "'Poppins', sans-serif",
//         marginTop: "56px",
//       }}
//     >
//       {popup.show && (
//         <PopupMessage
//           type={popup.type}
//           message={popup.message}
//           onClose={() => setPopup({ show: false, message: "", type: "" })}
//         />
//       )}

//       <h2 className="text-center fw-bold mb-5 text-primary">
//         Add New Activity
//       </h2>

//       <form onSubmit={handleSubmit} className="mx-auto" style={{ maxWidth: "1300px" }}>
//         <div className="row g-4">

//           {/* Activity Name */}
//           <div className="col-md-6">
//             <label className="form-label fw-semibold text-primary">Activity Name</label>
//             <input type="text" name="activityName" className="form-control border-primary shadow-sm" placeholder="Enter The Activity Name" value={form.activityName} onChange={handleChange} />
//           </div>

//           {/* Activity Type */}
//           <div className="col-md-6">
//             <label className="form-label fw-semibold text-primary">Activity Type</label>
//             <select className="form-select border-primary shadow-sm" name="activityType" value={form.activityType} onChange={handleChange}>
//               <option value="">Select Activity Type</option>
//               <option>SPORTS</option>
//               <option>ACADEMIC</option>
//               <option>ENTERTAINMENT</option>
//               <option>COMPETITIONS</option>
//             </select>
//           </div>

//           {/* Location Button */}
//           <div className="col-md-6">
//             <label className="form-label fw-semibold text-primary">Location / Venue</label>
//             <div className="d-flex gap-2 align-items-center">
//               <input type="text" name="locationName" className="form-control border-primary shadow-sm" placeholder="Enter location manually (e.g. Building C)" value={form.locationName} onChange={handleChange} />
//               <button type="button" className={`btn fw-semibold shadow-sm d-flex align-items-center gap-2 ${locationPicked ? "btn-success" : "btn-outline-primary"}`} onClick={() => setShowMap(true)} style={{ transition: "all 0.3s ease", borderRadius: "50px", whiteSpace: "nowrap" }}>
//                 {locationPicked ? <><CheckCircleFill /> Picked</> : <><GeoAltFill /> Pick</>}
//               </button>
//             </div>
//           </div>

//           {/* Gender */}
//           <div className="col-md-3">
//             <label className="form-label fw-semibold text-primary">Gender</label>
//             <select className="form-select border-primary shadow-sm" name="targetedGender" value={form.targetedGender} onChange={handleChange}>
//               <option value="">Select Gender</option>
//               <option>ALL</option>
//               <option>MALE</option>
//               <option>FEMALE</option>
//             </select>
//           </div>

//           {/* Capacity */}
//           <div className="col-md-3">
//             <label className="form-label fw-semibold text-primary">Capacity</label>
//             <input type="number" name="capacity" className="form-control border-primary shadow-sm" placeholder="Limit" value={form.capacity} onChange={handleChange} min="1" />
//           </div>

//           {/* College */}
//           <div className="col-md-6">
//             <label className="form-label fw-semibold text-primary">College</label>
//             <select className="form-select border-primary shadow-sm" name="targetedFaculty" value={form.targetedFaculty} disabled>
//               <option value={userFaculty}>{userFaculty}</option>
//             </select>
//           </div>

//           {/* Level */}
//           <div className="col-md-6">
//             <label className="form-label fw-semibold text-primary">Level</label>
//             <select className="form-select border-primary shadow-sm" name="targetedLevel" value={form.targetedLevel} onChange={handleChange}>
//               <option value="">Select Level</option>
//               {filteredLevels.map((lvl) => (<option key={lvl} value={lvl}>{lvl}</option>))}
//             </select>
//           </div>

//           {/* Date Fields */}
//           <div className="col-md-6">
//             <label className="form-label fw-semibold text-primary">Registration Opens At</label>
//             <input type="datetime-local" name="registrationOpenAt" className="form-control border-primary shadow-sm" value={form.registrationOpenAt} onChange={handleChange} />
//           </div>
//           <div className="col-md-6">
//             <label className="form-label fw-semibold text-primary">Registration Closes At</label>
//             <input type="datetime-local" name="registrationCloseAt" className="form-control border-primary shadow-sm" value={form.registrationCloseAt} onChange={handleChange} />
//           </div>
//           <div className="col-md-6">
//             <label className="form-label fw-semibold text-primary">Activity Starts At</label>
//             <input type="datetime-local" name="activityStartAt" className="form-control border-primary shadow-sm" value={form.activityStartAt} onChange={handleChange} />
//           </div>
//           <div className="col-md-6">
//             <label className="form-label fw-semibold text-primary">Activity Ends At</label>
//             <input type="datetime-local" name="activityEndAt" className="form-control border-primary shadow-sm" value={form.activityEndAt} onChange={handleChange} />
//           </div>

//           {/* Description */}
//           <div className="col-md-6">
//             <label className="form-label fw-semibold text-primary">Activity Description</label>
//             <textarea className="form-control border-primary shadow-sm" name="description" rows={4} placeholder="Enter Activity Description" value={form.description} onChange={handleChange} />
//           </div>

//           {/* Instructions */}
//           <div className="col-md-6">
//             <label className="form-label fw-semibold text-primary">Special Instructions</label>
//             <textarea className="form-control border-primary shadow-sm" name="specialInstructions" rows={4} placeholder="Enter Special Instructions" value={form.specialInstructions} onChange={handleChange} />
//           </div>

//           {/* Links */}
//           <div className="col-12">
//             <label className="form-label fw-semibold text-primary">Related Links</label>
//             {form.links.map((link, index) => (
//               <div key={index} className="d-flex gap-2 mb-2 align-items-center">
//                 <input type="text" className="form-control border-primary shadow-sm" placeholder="Link Title (e.g., Intro Video)" value={link.title} onChange={(e) => handleLinkChange(index, "title", e.target.value)} />
//                 <input type="url" className="form-control border-primary shadow-sm" placeholder="Enter URL" value={link.url} onChange={(e) => handleLinkChange(index, "url", e.target.value)} />
//                 <Button variant="danger" onClick={() => handleRemoveLink(index)}><Trash3Fill /></Button>
//               </div>
//             ))}
//             <Button variant="outline-primary" onClick={handleAddLink}><PlusCircle className="me-2" /> Add Link</Button>
//           </div>

//           {/* Upload Poster */}
//           <div className="col-md-6">
//             <label className="form-label fw-semibold text-primary">Upload Poster</label>
//             <div className="border border-primary border-2 rounded-3 d-flex flex-column align-items-center justify-content-center position-relative overflow-hidden shadow-sm" style={{ minHeight: "200px", backgroundColor: "#f0f8ff", borderStyle: "dashed", transition: "0.3s" }}>
//               {form.activityPoster && <img src={URL.createObjectURL(form.activityPoster)} alt="Preview" className="w-100 h-100 object-fit-cover position-absolute top-0 start-0" style={{ opacity: 0.3 }} />}
//               <input type="file" accept="image/*" onChange={handlePosterChange} id="posterInput" className="d-none" />
//               <div className="position-relative z-2 d-flex gap-2">
//                 {!form.activityPoster ? (
//                   <button type="button" onClick={() => document.getElementById("posterInput").click()} className="btn btn-outline-primary fw-semibold">Upload Poster</button>
//                 ) : (
//                   <>
//                     <button type="button" className="btn btn-outline-success fw-semibold" onClick={() => setShowPosterPreview(true)}>View Poster</button>
//                     <button type="button" onClick={() => document.getElementById("posterInput").click()} className="btn btn-outline-primary fw-semibold">Change Poster</button>
//                   </>
//                 )}
//               </div>
//             </div>
//           </div>

//           {/* Submit */}
//           <div className="col-12 text-center mt-4">
//             <button type="submit" className="btn btn-primary fw-semibold px-5 py-2 shadow-sm" disabled={loading} style={{ borderRadius: "50px" }}>
//               {loading ? <><ArrowClockwise className="spin me-2" /> Sending...</> : <><Send className="me-2" /> Submit Activity</>}
//             </button>
//           </div>
//         </div>
//       </form>

//       {/* --- Map Modal with High Precision GPS --- */}
//       <Modal show={showMap} onHide={() => setShowMap(false)} size="lg" centered backdrop="static">
//         <Modal.Header closeButton className="bg-primary text-white" style={{ borderBottom: "none" }}>
//           <Modal.Title className="fw-semibold">Select Activity Location (High Precision)</Modal.Title>
//         </Modal.Header>

//         <Modal.Body className="p-3" style={{ height: "500px", backgroundColor: "#f8f9fa", borderRadius: "8px" }}>
//           <div className="d-flex flex-wrap justify-content-center justify-content-md-end gap-3 mb-3" style={{ zIndex: 1000 }}>
//             {/* 📍 High Accuracy GPS Button */}
//             <Button
//               variant="outline-primary"
//               className="fw-semibold d-flex align-items-center gap-2 shadow-sm"
//               style={{ borderRadius: "30px", minWidth: "190px", transition: "all 0.3s ease" }}
//               onClick={() => {
//                 if (navigator.geolocation) {
//                   // 🔥 Critical: Request High Accuracy
//                   const options = {
//                     enableHighAccuracy: true,
//                     timeout: 10000,
//                     maximumAge: 0
//                   };

//                   setPopup({ show: true, message: "Requesting high-accuracy GPS...", type: "info" });

//                   navigator.geolocation.getCurrentPosition(
//                     (position) => {
//                       const { latitude, longitude } = position.coords;
//                       setPopup({ show: true, message: "Location detected! Drag marker to adjust.", type: "info" });
//                       setSelectedPosition({ lat: latitude, lng: longitude });
//                       setForm((prev) => ({ ...prev, coordinates: [latitude, longitude] }));
//                     },
//                     (error) => setPopup({ show: true, message: "Failed to get location. Enable GPS.", type: "error" }),
//                     options
//                   );
//                 } else {
//                   setPopup({ show: true, message: "Geolocation not supported.", type: "error" });
//                 }
//               }}
//             >
//               <GeoAltFill /> Use My Location
//             </Button>

//             <Button
//               variant={selectedPosition ? "success" : "outline-secondary"}
//               disabled={!selectedPosition}
//               className={`fw-semibold d-flex align-items-center gap-2 shadow-sm ${selectedPosition ? "btn-success" : ""}`}
//               style={{ borderRadius: "30px", minWidth: "190px", transition: "all 0.3s ease" }}
//               onClick={handleConfirmLocation}
//             >
//               <CheckCircleFill /> Confirm Selection
//             </Button>
//           </div>

//           <div className="rounded-4 overflow-hidden shadow-sm" style={{ height: "100%", minHeight: "400px", border: "2px solid #0d6efd33" }}>
//             <MapContainer center={selectedPosition || [30.0444, 31.2357]} zoom={14} style={{ height: "100%", width: "100%" }} className="rounded-4">
//               <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap contributors" />
//               <LocationPicker onSelect={handleMapClick} />

//               {/* ✅ Auto Zoom */}
//               {selectedPosition && <MapRecenter lat={selectedPosition.lat} lng={selectedPosition.lng} />}

//               {/* ✅ Draggable Marker */}
//               {selectedPosition && (
//                 <Marker
//                   draggable={true}
//                   eventHandlers={eventHandlers}
//                   position={[selectedPosition.lat, selectedPosition.lng]}
//                   ref={markerRef}
//                   icon={markerIcon}
//                 />
//               )}
//             </MapContainer>
//           </div>
//         </Modal.Body>
//         <Modal.Footer className="d-flex justify-content-center justify-content-md-end gap-3 bg-light" style={{ borderTop: "none" }}>
//           <Button variant="secondary" onClick={() => setShowMap(false)} className="fw-semibold rounded-pill px-4">Close</Button>
//         </Modal.Footer>
//         <style>{`@media (max-width: 768px) { .leaflet-container { height: 350px !important; } .modal-dialog { margin: 1rem; } }`}</style>
//       </Modal>

//       {/* Preview Modal */}
//       <Modal show={showPosterPreview} onHide={() => setShowPosterPreview(false)} size="lg" centered>
//         <Modal.Header closeButton><Modal.Title>Poster Preview</Modal.Title></Modal.Header>
//         <Modal.Body className="text-center">{form.activityPoster && <img src={URL.createObjectURL(form.activityPoster)} alt="Poster Preview" className="img-fluid rounded shadow-sm" />}</Modal.Body>
//       </Modal>

//       <ChatbotButton />
//     </div>
//   );
// }
